import React, { useEffect, useMemo, useState, useRef } from 'react';
import { checkMot } from '../../../ressources/functionJS/checkMot';
import { FiExternalLink } from 'react-icons/fi';
import S from './Widget.module.scss';
import WidgetHeaderControls from './WidgetHeaderControls';
import MapPreview from './MapPreview';
import AffichageVue from '../../Vue/AffichageVue/AffichageVue';
import Popup from '../../UI/PopupVue/Popup';

const ORIGINAL_WIDTH = 900;
const ORIGINAL_HEIGHT = 540;
const DEFAULT_CONTAINER_WIDTH = 480;

const WidgetPlan = ({
  vuesFavorites = [],
  headerContent = null,
  showHeader = true,
  viewId = null, // ID de la vue pour rendre AffichageVue directement
  loading = false,
}) => {
  const hasVue = vuesFavorites && vuesFavorites.length > 0;
  const [selectedVueIndex, setSelectedVueIndex] = useState('0');
  const [afficherPopupVue, setAfficherPopupVue] = useState(false);
  const ouvrirPopupVue = () => setAfficherPopupVue(true);
  const fermerPopupVue = () => setAfficherPopupVue(false);
  const vuesLength = vuesFavorites?.length || 0;

  const currentVue = hasVue
    ? vuesFavorites[Number(selectedVueIndex)] || vuesFavorites[0]
    : null;

  const resolvedViewId = viewId || currentVue?.view_id;

  // Refs e estados
  const widgetRef = useRef(null);
  const widgetContentRef = useRef(null);
  const miniatureContainerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(DEFAULT_CONTAINER_WIDTH);
  const [containerHeight, setContainerHeight] = useState(ORIGINAL_HEIGHT);
  const [isVisible, setIsVisible] = useState(false);
  const [mapReady, setMapReady] = useState(false);

  // Monter AffichageVue uniquement quand le widget entre dans le viewport
  useEffect(() => {
    const el = widgetRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Reset mapReady when view changes
  useEffect(() => {
    if (isVisible) setMapReady(false);
  }, [resolvedViewId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fade out skeleton after map has had time to load
  useEffect(() => {
    if (!isVisible) return;
    const timer = setTimeout(() => setMapReady(true), 900);
    return () => clearTimeout(timer);
  }, [isVisible, resolvedViewId]);

  // Observer les dimensions du widgetContent dès le mount (avant isVisible)
  useEffect(() => {
    const el = widgetContentRef.current;
    if (!el) return;

    const update = () => {
      const w = el.offsetWidth - 20;  // subtract padding (10px each side)
      const h = el.offsetHeight - 20;
      if (w > 0) setContainerWidth(w);
      if (h > 0) setContainerHeight(h);
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (!hasVue) {
      setSelectedVueIndex('0');
      return;
    }

    const currentIndex = Number(selectedVueIndex);
    if (currentIndex >= vuesLength) {
      setSelectedVueIndex('0');
    }
  }, [hasVue, vuesLength, selectedVueIndex]);

  const vueOptions = useMemo(() => {
    if (!hasVue || vuesLength < 2) {
      return [];
    }

    return vuesFavorites.map((vue, index) => ({
      value: String(index),
      label: vue.view_name || `${checkMot('plan')} ${index + 1}`,
    }));
  }, [hasVue, vuesFavorites, vuesLength]);

  const resolveCenter = (vue) => {
    if (!vue) return null;
    if (Array.isArray(vue.center) && vue.center.length === 2) return vue.center;
    if (vue.lat && vue.lng) return [vue.lat, vue.lng];
    if (vue.latitude && vue.longitude) return [vue.latitude, vue.longitude];
    if (vue.center_lat && vue.center_lng)
      return [vue.center_lat, vue.center_lng];
    return null;
  };

  const mapCenter = resolveCenter(currentVue) || [48.8566, 2.3522];
  const mapZoom = currentVue?.zoom || 12;

  const resolvedHeader =
    headerContent ||
    (showHeader ? (
      <WidgetHeaderControls
        titleKey="plan"
        selectOptions={vueOptions}
        selectedValue={selectedVueIndex}
        onSelectChange={setSelectedVueIndex}
        headerAction={
          resolvedViewId ? (
            <button
              className={S.widgetMenuBtn}
              title={checkMot('voir_plus')}
              onClick={ouvrirPopupVue}
            >
              <FiExternalLink size={18} />
            </button>
          ) : null
        }
      />
    ) : null);

  // Afficher la miniature d'AffichageVue
  const renderPlanContent = () => {
    if (resolvedViewId) {
      const scaleX = containerWidth / ORIGINAL_WIDTH;
      const scaleY = containerHeight / ORIGINAL_HEIGHT;
      const scale = Math.min(scaleX, scaleY);
      const scaledHeight = ORIGINAL_HEIGHT * scale;

      return (
        <div
          ref={miniatureContainerRef}
          className={S.dashboardMiniatureContainer}
          style={{ height: `${scaledHeight}px` }}
        >
          {isVisible && (
            <div
              className={S.dashboardMiniatureInner}
              style={{
                width: `${ORIGINAL_WIDTH}px`,
                height: `${ORIGINAL_HEIGHT}px`,
                transform: `scale(${scale})`,
              }}
            >
              <AffichageVue
                key={`plan-${resolvedViewId}`}
                view_id={resolvedViewId}
                mapContainerId={`mapPlan-${resolvedViewId}`}
              />
            </div>
          )}
          <div
            className={S.miniatureSkeletonOverlay}
            style={{ opacity: mapReady ? 0 : 1 }}
          />
        </div>
      );
    }

    // Fallback vers MapPreview si pas de view_id
    if (hasVue) {
      return (
        <div className={S.planContainer}>
          <div className={S.planPreview}>
            <MapPreview
              center={mapCenter}
              zoom={mapZoom}
              className={S.leafletContainer}
              interactive={true}
            />
          </div>
        </div>
      );
    }

    // Empty state
    return (
      <div className={S.emptyState}>
        <div className={S.mapPlaceholder}>
          <MapPreview
            center={[48.8566, 2.3522]}
            zoom={3}
            className={S.leafletContainer}
            interactive={true}
          />
        </div>
      </div>
    );
  };

  return (
    <>
    <div ref={widgetRef} className={[S.widget, S.widgetLarge].join(' ')}>
      {resolvedHeader}
      <div ref={widgetContentRef} className={S.widgetContent}>
        {loading && !hasVue ? (
          <div className={S.widgetLoadingState}>
            <div className={S.widgetLoadingSpinner} />
            <span className={S.widgetLoadingText}>{checkMot('chargement_en_cours')}</span>
          </div>
        ) : (
          <>
            {renderPlanContent()}
          </>
        )}
      </div>
    </div>
    <Popup
      sansBouton={true}
      eventAuClique={ouvrirPopupVue}
      eventALaFermeture={fermerPopupVue}
      showModal={afficherPopupVue}
    >
      {afficherPopupVue && <AffichageVue view_id={resolvedViewId} />}
    </Popup>
    </>
  );
};

export default WidgetPlan;
